// Apolo IDE — S2 · Task-driven executor (a peça-mãe) + S4 · checkpoints ao vivo.
// @arch:pure-engine
//
// Fecha o gap central do S2-03-PLAN (E4) e do S4-03-PLAN (E2): o executor que
// roda o DAG de Tasks pela ordem de DEPENDÊNCIA real (nextRunnable do taskGraph),
// verifica honest-green por task via ORACLE (nunca confidence — A LEI), porta o
// gate "task N green libera N+1", e integra os CHECKPOINTS (salva no verde,
// restaura o último verde N-1 no vermelho). Emite o contrato de eventos
// CONGELADO. Orquestrador PURO sobre seams injetados (testável sem LLM/FS):
//
//   runTask(task)            → roda o agente p/ a task → result (qualquer shape)
//   verifyTask(task, result) → honest-green oracle → { green:boolean, evidence? }
//   saveCheckpoint(task)     → id do checkpoint (estado verde) — opcional
//   restoreCheckpoint(id)    → restaura estado (fail → N-1) — opcional
//   onEvent(evt)             → sink do contrato de eventos
//   signal                   → AbortSignal (Stop)
//
// É uma CAMADA acima do runLocalAgentLoop (zero mudança no motor), igual o
// harness A/B é camada acima do oracle.

import { validateGraph, nextRunnable } from './taskGraph.js';
import { abortReasonOf } from './loop/abortReason.js';

/** Contrato de eventos CONGELADO (S2 frozen) + WSX task_blocked + fix B task_reset (aditivos).
 *  PERF-2026-07: task_retry adicionado — oracle-red pode tentar de novo com feedback. */
export const TASK_EVENTS = Object.freeze([
  'task_started', 'task_oracle_pass', 'task_oracle_fail', 'task_blocked', 'task_reset', 'task_retry',
  'graph_done', 'graph_blocked',
]);

/** PERF-2026-07 — máx de retries por task quando o oracle reprova (task_failed).
 *  Cada retry injeta o feedback do oracle como contexto extra pro agente corrigir.
 *  2 = até 3 execuções totais (1 inicial + 2 retries com feedback). */
const DEFAULT_TASK_RETRY_LIMIT = 2;

const isAborted = (signal) => !!(signal && signal.aborted);

// fix B — wave paralela ATÔMICA. Por que atômica: os checkpoints são snapshots do
// TREE INTEIRO (git stash create) — com 2+ tasks editando ao mesmo tempo não existe
// rollback cirúrgico de UMA task. Então: salva um checkpoint PRÉ-WAVE (review
// adversarial: sem ele o rollback da 1ª wave era restore(null) = NO-OP e o retry
// serial rodava sobre tree contaminado), roda a wave em paralelo; TUDO verde ⇒
// um checkpoint pós-wave; senão ⇒ restaura o PRÉ-WAVE e decide task a task:
//   • result.blocked (timeout/permissão/Stop) ⇒ 'blocked' de verdade — SEM retry
//     grátis (paridade com o serial; a mensagem do fix H diz "bloqueada" e é);
//   • os demais ⇒ 'pending' + _serialOnly (re-rodam pelo caminho serial de sempre).
// Stop do usuário mid-wave ⇒ todas viram 'blocked' (aborted) + restore — o serial
// faz o mesmo; NADA fica preso em 'running' nem sujo no tree.
// Extraída do runTaskGraph (ratchet de complexidade); recebe o contexto por seams.
// Retorna 'green' | 'rolledback' | 'aborted'.
async function runParallelWave(wave, ctx) {
  const { runTask, verifyTask, signal, emit, saveCheckpoint, restoreCheckpoint, saveGreen, done, ran, blocked } = ctx;
  let preWave = null;
  try { preWave = await saveCheckpoint({ id: `pre_wave_${wave.map((t) => t.id).join('_')}` }); } catch { /* sem pré-wave ⇒ aborta o paralelo */ }
  if (!preWave) return 'no_checkpoint'; // não dá pra garantir atomicidade ⇒ caller cai no serial
  const restorePreWave = async () => { try { await restoreCheckpoint(preWave); } catch { /* best-effort */ } };

  for (const t of wave) { t.status = 'running'; emit('task_started', { taskId: t.id }); }
  const results = await Promise.all(wave.map(async (t) => {
    try { return await runTask(t, { parallel: true }); } catch (e) { return { ok: false, error: e?.message || String(e) }; }
  }));

  if (isAborted(signal)) {
    // Stop do usuário: mesmo tratamento do serial — blocked (recuperável), tree limpo.
    await restorePreWave();
    for (const t of wave) {
      t.status = 'blocked'; blocked.push(t.id); ran.push(t.id);
      emit('task_blocked', { taskId: t.id, reason: abortReasonOf(signal) });
    }
    return 'aborted';
  }

  const verdicts = await verifyWave(wave, results, verifyTask);
  if (verdicts) {
    for (let i = 0; i < wave.length; i += 1) {
      const t = wave[i];
      t.status = 'done'; done.push(t.id); ran.push(t.id);
      emit('task_oracle_pass', { taskId: t.id, evidence: verdicts[i].evidence });
    }
    // UM checkpoint pós-wave: snapshots intermediários seriam idênticos (os edits
    // da wave inteira já estão no tree quando o 1º verify roda).
    await saveGreen(wave[wave.length - 1]);
    return 'green';
  }

  await restorePreWave(); // estado nunca fica sujo — volta pro exato pré-wave
  for (const t of wave) {
    const r = results[wave.indexOf(t)];
    if (r && r.blocked) {
      // Bloqueio REAL (task_timeout/permissão): fica blocked como no serial — sem
      // rodada extra automática (senão o teto de 15min viraria 30 na prática).
      t.status = 'blocked'; blocked.push(t.id); ran.push(t.id);
      emit('task_blocked', { taskId: t.id, reason: r.blockedReason });
    } else {
      t.status = 'pending';
      t._serialOnly = true; // nunca re-entra em wave paralela (anti-loop do fallback)
      emit('task_reset', { taskId: t.id });
    }
  }
  return 'rolledback';
}

// Verifica a wave em ordem; devolve os verdicts SÓ quando TUDO é verde (senão null —
// qualquer bloqueio/vermelho condena a wave inteira; o rollback atômico decide).
async function verifyWave(wave, results, verifyTask) {
  if (results.some((r) => r && r.blocked)) return null;
  const verdicts = [];
  for (let i = 0; i < wave.length; i += 1) {
    let v;
    try { v = await verifyTask(wave[i], results[i]); } catch (e) { v = { green: false, evidence: { error: e?.message || String(e) } }; }
    if (!(v && v.green)) return null; // primeiro vermelho já condena a wave inteira
    verdicts.push(v);
  }
  return verdicts;
}

/**
 * PERF-2026-07 — Extrai feedback acionável do verdict do oracle para injetar no retry.
 * O agente recebe isto como contexto extra e sabe exatamente o que corrigir.
 * Pura + testável.
 */
function extractOracleFeedback(verdict) {
  if (!verdict || !verdict.evidence) return 'A verificação falhou sem detalhes. Revise a implementação.';
  const ev = verdict.evidence;
  // O evidence pode vir em vários formatos dependendo do verifier:
  // - OracleVerdict[] (array) — o shape PRODUZIDO por buildVerifyTask (evidence: o.verdicts).
  //   Cada item: { verifier:'lint'|'types'|'test', passed:boolean, evidence:string }
  // - { error: 'mensagem' } para verifiers que throw
  // - { stdout: '...', exitCode: 1 } para run_command verifiers
  // - { checks: [{ name, passed, detail }] } para verifiers estruturados
  // - string direta
  if (typeof ev === 'string') return ev;
  const parts = [];
  if (Array.isArray(ev)) {
    // buildVerifyTask retorna evidence = o.verdicts (um OracleVerdict[]). Sem este
    // branch, todos os checks abaixo (ev.error, ev.stdout, etc.) eram undefined num
    // array, parts ficava vazio, e o retry recebia um feedback genéric inútil — o
    // agente não sabia o que corrigir e falhava novamente, levando a task_failed.
    const failed = ev.filter((v) => v && v.passed === false);
    for (const v of failed) parts.push(`✗ [${v.verifier || 'unknown'}] ${v.evidence || 'falhou'}`);
  }
  if (ev.error) parts.push(`Erro: ${ev.error}`);
  if (ev.stdout) parts.push(`Saída:\n${String(ev.stdout).slice(0, 2000)}`);
  if (ev.stderr) parts.push(`Stderr:\n${String(ev.stderr).slice(0, 2000)}`);
  if (ev.exitCode && ev.exitCode !== 0) parts.push(`Exit code: ${ev.exitCode}`);
  if (Array.isArray(ev.checks)) {
    const failed = ev.checks.filter((c) => !c.passed);
    for (const c of failed) parts.push(`✗ ${c.name}: ${c.detail || 'falhou'}`);
  }
  if (ev.detail) parts.push(String(ev.detail));
  if (ev.message) parts.push(String(ev.message));
  return parts.length > 0
    ? `A verificação da task falhou. Corrija os problemas abaixo:\n${parts.join('\n')}`
    : 'A verificação falhou. Revise a implementação contra os critérios de aceitação.';
}

/**
 * PERF-2026-07 — Executa uma task com retry via oracle. Retorna o outcome:
 * 'green' (passou), 'failed' (esgotou retries), 'blocked' (não conseguiu executar).
 * Extraída do runTaskGraph para reduzir complexidade ciclomática.
 */
async function runTaskWithRetry(task, ctx) {
  const { runTask, verifyTask, emit, retryLimit, saveGreen, restoreGreen, done, failed, blocked, ran } = ctx;

  let result;
  try {
    result = await runTask(task);
  } catch (e) {
    result = { ok: false, error: e?.message || String(e) };
  }

  if (result && result.blocked) {
    task.status = 'blocked';
    blocked.push(task.id);
    ran.push(task.id);
    emit('task_blocked', { taskId: task.id, reason: result.blockedReason });
    await restoreGreen();
    return 'blocked';
  }

  let lastFeedback = null;
  for (let attempt = 0; attempt <= retryLimit; attempt += 1) {
    if (attempt > 0) {
      await restoreGreen();
      task.status = 'running';
      emit('task_retry', { taskId: task.id, attempt, feedback: lastFeedback });
      try {
        result = await runTask(task, { oracleFeedback: lastFeedback, retryAttempt: attempt });
      } catch (e) {
        result = { ok: false, error: e?.message || String(e) };
      }
      if (result && result.blocked) {
        task.status = 'blocked';
        blocked.push(task.id);
        ran.push(task.id);
        emit('task_blocked', { taskId: task.id, reason: result.blockedReason });
        await restoreGreen();
        return 'blocked';
      }
    }

    let verdict;
    try {
      verdict = await verifyTask(task, result);
    } catch (e) {
      verdict = { green: false, evidence: { error: e?.message || String(e) } };
    }

    if (verdict && verdict.green) {
      task.status = 'done';
      done.push(task.id);
      ran.push(task.id);
      emit('task_oracle_pass', { taskId: task.id, evidence: verdict.evidence });
      await saveGreen(task);
      return 'green';
    }

    lastFeedback = extractOracleFeedback(verdict);
    if (attempt === retryLimit) {
      task.status = 'failed';
      failed.push(task.id);
      ran.push(task.id);
      emit('task_oracle_fail', { taskId: task.id, evidence: verdict?.evidence, retries: attempt });
      await restoreGreen();
      return 'failed';
    }
  }
  return 'failed';
}

/**
 * Roda o grafo de tasks respeitando dependências + honest-green + checkpoints.
 * Muta `status` das tasks in-place ('running'|'done'|'failed') e devolve um
 * resumo. NUNCA marca uma task como done sem o oracle (verifyTask) devolver
 * green — confidence/model JAMAIS substituem o oracle (A LEI, testada).
 *
 * @param {{
 *   tasks: Array<{id:string, depends_on?:string[], status?:string, _serialOnly?:boolean}>,
 *   runTask: (task:object, runCtx?:object)=>Promise<any>|any,
 *   verifyTask: (task:object, result:any)=>Promise<{green:boolean, evidence?:any}>|{green:boolean, evidence?:any},
 *   saveCheckpoint?: (task:object)=>Promise<string>|string,
 *   restoreCheckpoint?: (checkpointId:string|null)=>Promise<void>|void,
 *   onEvent?: (evt:object)=>void,
 *   signal?: AbortSignal,
 *   concurrency?: number,
 *   retryLimit?: number,
 * }} cfg
 */
export async function runTaskGraph(cfg) {
  const {
    tasks, runTask, verifyTask,
    saveCheckpoint, restoreCheckpoint,
    onEvent = () => {}, signal,
    retryLimit = DEFAULT_TASK_RETRY_LIMIT,
    // RCA lentidão fix B — nº de tasks INDEPENDENTES rodando juntas numa wave.
    // 1 (default) = serial byte-idêntico ao de sempre. >1 liga a wave paralela
    // ATÔMICA (ver runParallelWave abaixo): paraleliza o caminho feliz, mas se
    // QUALQUER membro falha/bloqueia, a wave inteira faz rollback pro último
    // checkpoint verde e re-roda SERIAL — semântica final idêntica à serial.
    concurrency = 1,
  } = cfg || {};

  const list = Array.isArray(tasks) ? tasks : [];
  const emit = (type, extra) => { try { onEvent({ type, ...extra }); } catch { /* sink swallow */ } };

  // Gate de integridade: ciclo / ids duplicados ⇒ NÃO roda nada (graph_blocked).
  const v = validateGraph(list);
  if (!v.ok) {
    emit('graph_blocked', { reason: v.cycle ? 'cycle' : 'invalid_graph', detail: v });
    return { ran: [], done: [], failed: [], blocked: true, reason: v.cycle ? 'cycle' : 'invalid_graph' };
  }

  const done = [];
  const failed = [];
  const blocked = []; // WSX — tasks que NÃO conseguiram executar (permissão/timeout/abort): recuperável, NÃO é vermelho de oracle
  const ran = [];
  let lastGreenCheckpoint = null;

  // S4 — checkpoint helpers (extraídos): salvam no verde / restauram o último verde
  // no não-verde. best-effort (falha nunca bloqueia o run) e sem try aninhado nos
  // branches (mantém o corpo do loop raso + DRY para os 3 pontos save/restore).
  const saveGreen = async (task) => {
    if (typeof saveCheckpoint !== 'function') return;
    try { lastGreenCheckpoint = await saveCheckpoint(task); } catch { /* best-effort */ }
  };
  const restoreGreen = async () => {
    if (typeof restoreCheckpoint !== 'function') return;
    try { await restoreCheckpoint(lastGreenCheckpoint); } catch { /* best-effort */ }
  };

  // fix B (review adversarial) — paralelismo EXIGE os dois seams de checkpoint:
  // sem snapshot pré-wave não existe rollback atômico (restore(null) era no-op e o
  // retry serial rodava sobre tree contaminado). Sem seams ⇒ cap efetivo 1 (serial).
  const canParallel = typeof saveCheckpoint === 'function' && typeof restoreCheckpoint === 'function';
  const cap = canParallel ? Math.max(1, Math.floor(Number(concurrency) || 1)) : 1;
  const waveCtx = { runTask, verifyTask, signal, emit, retryLimit, saveCheckpoint, restoreCheckpoint, saveGreen, restoreGreen, done, failed, ran, blocked };
  let parallelDead = false; // pré-wave falhou uma vez ⇒ desiste do paralelo pro run inteiro

  // Loop por ONDAS implícitas: a cada volta pega os runnable (deps todas green).
  // Sequencial dentro da volta pra honrar "N green libera N+1" + semântica de
  // checkpoint determinística. Termina quando não há mais runnable.
  // Anti-loop: cada task só roda uma vez (vira terminal).
  for (;;) {
    if (isAborted(signal)) {
      const reason = abortReasonOf(signal);
      emit('graph_blocked', { reason });
      return { ran, done, failed, blocked: true, reason };
    }

    const runnable = nextRunnable(list);
    if (runnable.length === 0) break;

    // fix B — a wave paralela só entra quando há 2+ runnable e NENHUM veio de um
    // rollback (_serialOnly): fallback serial primeiro, paralelismo depois.
    if (cap > 1 && !parallelDead && runnable.length > 1 && runnable.every((t) => !t._serialOnly)) {
      const outcome = await runParallelWave(runnable.slice(0, cap), waveCtx);
      if (outcome === 'aborted') {
        const reason = abortReasonOf(signal);
        emit('graph_blocked', { reason });
        return { ran, done, failed, blocked: true, reason };
      }
      if (outcome !== 'no_checkpoint') continue; // green/rolledback: próxima volta decide
      parallelDead = true; // snapshot pré-wave falhou ⇒ serial daqui pra frente
    }

    let madeProgress = false;
    for (const task of runnable) {
      if (isAborted(signal)) {
        const reason = abortReasonOf(signal);
        emit('graph_blocked', { reason });
        return { ran, done, failed, blocked: true, reason };
      }

      // Honest-green with retry: o ORACLE é o único juiz. (A LEI — sem confidence.)
      // PERF-2026-07 — oracle-red faz retry com feedback antes de bloquear o DAG.
      // runTaskWithRetry encapsula: runTask → blocked check → oracle → retry loop.
      task.status = 'running';
      emit('task_started', { taskId: task.id });
      const outcome = await runTaskWithRetry(task, waveCtx);
      if (outcome === 'failed') break; // fail bloqueia dependentes
      madeProgress = true;
    }

    if (!madeProgress) break; // segurança anti-loop
  }

  // Veredito do grafo: tudo terminou? algum falhou, bloqueou ou ficou inalcançável?
  const isTerminal = (t) => t.status === 'done' || t.status === 'failed' || t.status === 'blocked';
  const allTerminal = list.every(isTerminal);
  const isBlocked = failed.length > 0 || blocked.length > 0 || !allTerminal;
  if (isBlocked) {
    const unreachable = list.filter((t) => !isTerminal(t)).map((t) => t.id);
    // Prioridade do motivo: um vermelho de oracle REAL manda ('task_failed'); senão um
    // bloqueio RECUPERÁVEL ('blocked_permission' — permissão/timeout/abort); senão só
    // dependentes inalcançáveis ('unreachable'). dagOutcome já mapeia blocked→not_run.
    const reason = failed.length ? 'task_failed' : (blocked.length ? 'blocked_permission' : 'unreachable');
    emit('graph_blocked', { reason, failed: [...failed], blocked: [...blocked], unreachable });
    return { ran, done, failed, blocked: true, reason, blockedTasks: [...blocked], unreachable };
  }
  emit('graph_done', { done: [...done] });
  return { ran, done, failed, blocked: false };
}
